import { type PayloadAction, type ThunkAction, type UnknownAction, createSlice } from '@reduxjs/toolkit';

import { serverEvent } from '@proton/account/eventLoop';
import type { ProtonThunkArguments } from '@proton/redux-shared-store-types/interface';
import { createAsyncModelThunk, handleAsyncModel, previousSelector } from '@proton/redux-utilities/creator';
import { getInitialModelState } from '@proton/redux-utilities/initialModelState';
import type { ModelState } from '@proton/redux-utilities/initialModelState/interface';
import { CacheType } from '@proton/redux-utilities/interface';
import type { MailEventV6Response } from '@proton/shared/lib/api/events';
import { getByIds, getContactGroup, getFolders, getLabels, getSystemFolders } from '@proton/shared/lib/api/labels';
import { updateCollectionAsyncV6 } from '@proton/shared/lib/eventManager/updateCollectionAsyncV6';
import { type UpdateCollectionV6, updateCollectionV6 } from '@proton/shared/lib/eventManager/updateCollectionV6';
import updateCollection, { sortCollection } from '@proton/shared/lib/helpers/updateCollection';
import type { Api } from '@proton/shared/lib/interfaces/Api';
import type { Category } from '@proton/shared/lib/interfaces/Category';
import { removeById } from '@proton/utils/removeById';
import { upsertById } from '@proton/utils/upsertById';

const name = 'categories' as const;

const extractLabels = ({ Labels = [] }) => Labels;

const getLabelsModel = async (api: Api) => {
    const [systemFolders = [], folders = [], labels = [], contactGroups = []] = await Promise.all([
        api(getSystemFolders()).then(extractLabels),
        api(getFolders()).then(extractLabels),
        api(getLabels()).then(extractLabels),
        api(getContactGroup()).then(extractLabels),
    ]);

    return sortCollection('Order', [...systemFolders, ...folders, ...labels, ...contactGroups]);
};

export interface CategoriesState {
    [name]: ModelState<Category[]>;
}

type SliceState = CategoriesState[typeof name];
type Model = NonNullable<SliceState['value']>;

export const selectCategories = (state: CategoriesState) => state[name];

const modelThunk = createAsyncModelThunk<Model, CategoriesState, ProtonThunkArguments>(`${name}/fetch`, {
    miss: async ({ extraArgument }) => {
        return getLabelsModel(extraArgument.api);
    },
    previous: previousSelector(selectCategories),
});

const initialState = getInitialModelState<Model>();
const slice = createSlice({
    name,
    initialState,
    reducers: {
        eventLoopV6: (state, action: PayloadAction<UpdateCollectionV6<Category>>) => {
            if (state.value) {
                state.value = sortCollection('Order', updateCollectionV6(state.value, action.payload));
            }
        },
        stale: (state) => {
            if (!state.value) {
                return;
            }
            state.meta.fetchedEphemeral = undefined;
        },
        upsertCategory: (state, action: PayloadAction<Category>) => {
            if (!state.value) {
                return;
            }
            state.value = upsertById(state.value, action.payload, 'ID');
        },
        deleteCategory: (state, action: PayloadAction<Category>) => {
            if (!state.value) {
                return;
            }
            state.value = removeById(state.value, action.payload, 'ID');
        },
    },
    extraReducers: (builder) => {
        handleAsyncModel(builder, modelThunk);
        builder.addCase(serverEvent, (state, action) => {
            if (state.value && action.payload.Labels) {
                state.value = sortCollection(
                    'Order',
                    updateCollection({
                        model: state.value,
                        events: action.payload.Labels,
                        itemKey: 'Label',
                        // Event updates don't return e.g. ParentID so it's better that the old value gets completely dropped instead of merged
                        merge: (_, b) => b as Category,
                    })
                );
            }
        });
    },
});

export const categoriesReducer = { [name]: slice.reducer };
export const categoriesThunk = modelThunk.thunk;
export const categoriesActions = slice.actions;

export const getCategory = async (api: Api, ID: string) => {
    const { Labels } = await api<{ Labels: Category[] }>(getByIds([ID]));
    return Object.values(Labels)[0];
};

export const categoriesEventLoopV6Thunk = ({
    event,
    api,
}: {
    event: MailEventV6Response;
    api: Api;
}): ThunkAction<Promise<void>, CategoriesState, ProtonThunkArguments, UnknownAction> => {
    return async (dispatch) => {
        await updateCollectionAsyncV6({
            events: event.Labels,
            get: (ID) => getCategory(api, ID),
            refetch: () => dispatch(categoriesThunk({ cache: CacheType.None })),
            update: (result) => dispatch(categoriesActions.eventLoopV6(result)),
        });
    };
};
